import { Copy, Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useRevealIban } from '../api'
import { formatIban } from '../iban'
import { FINANCE_IBAN_PERM } from '../permissions'
import { toastError } from '../ui'

/** Maskeli IBAN; yetkili kullanıcı "Göster" ile tamamını açar (denetim kaydına yazılır), kopyalayabilir. */
export function SensitiveIban({ target, id, masked }: { target: 'account' | 'party'; id: string; masked: string }) {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const reveal = useRevealIban()
  const [full, setFull] = useState<string | null>(null)
  if (!masked) return <span className="text-muted-foreground">—</span>
  const canWrite = can(FINANCE_IBAN_PERM)
  const actor = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || '—'

  const show = () => reveal.mutate({ target, id, actor }, { onSuccess: (v) => setFull(v), onError: toastError })
  const copy = () => {
    if (!full) return
    void navigator.clipboard
      .writeText(full)
      .then(() => toast.success(t('finance.iban.copied')))
      .catch(() => toast.error(t('errors.generic')))
  }

  return (
    <span className={cn('inline-flex h-7 max-w-full items-center gap-0.5 rounded-md border bg-muted/40 pl-2 align-middle', canWrite ? 'pr-0.5' : 'pr-2')}>
      <span className="min-w-0 truncate whitespace-nowrap font-mono text-xs tracking-wide" title={full ? formatIban(full) : undefined}>
        {full ? formatIban(full) : masked}
      </span>
      {canWrite ? (
        <span className="flex shrink-0 items-center">
          {full ? (
            <>
              <Button type="button" size="icon" variant="ghost" className="size-6" onClick={copy} aria-label={t('finance.iban.copy')}>
                <Copy className="size-3.5" />
              </Button>
              <Button type="button" size="icon" variant="ghost" className="size-6" onClick={() => setFull(null)} aria-label={t('finance.iban.hide')}>
                <EyeOff className="size-3.5" />
              </Button>
            </>
          ) : (
            <Button type="button" size="icon" variant="ghost" className="size-6" onClick={show} disabled={reveal.isPending} aria-label={t('finance.iban.show')} title={t('finance.iban.showHint')}>
              {reveal.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Eye className="size-3.5" />}
            </Button>
          )}
        </span>
      ) : null}
    </span>
  )
}
